/**
 * useRandomAccessData — React Query-powered page cache for virtual scroll.
 *
 * Architecture:
 * - One `useQuery` for the anchor page (page 0) — drives loading/transition states
 * - `queryClient.prefetchQuery` for background pages — triggered by scroll
 * - Cache subscription via `queryClient.getQueryCache().subscribe()` to detect
 *   when background pages arrive (triggers re-render so getRow sees fresh data)
 * - LRU tracker bounds which pages stay cached (memory control)
 * - `getRow(index)` reads directly from React Query cache — O(1) lookup
 */

import { useCallback, useEffect, useMemo, useRef, useReducer } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import type { InternalDataSourceConfig } from '../schema/types';
import type { FetchParams } from '../../hooks/useRemoteDataV2';
import { hasActiveQuery } from '../../hooks/useRemoteDataV2';
import { getToken } from '../../utils/getToken';
import {
  buildRandomAccessQuerySignature,
  computePageRange,
  parseListResponse,
} from '../../utils/randomAccessHelpers';
import { buildQueryParams, serializeQueryParams } from '../../utils/remoteDataHelpers';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type RandomAccessStatus = 'idle' | 'initializing' | 'transitioning' | 'fetching';

export interface RandomAccessConfig {
  dataSource: Readonly<InternalDataSourceConfig> | null;
  fetchParams: FetchParams;
  axiosInstance?: { get: Function; post: Function; request: Function };
  refreshGeneration: number;
  externalDataProvided: boolean;
  pageSize: number;
  maxCachedPages: number;
  fetchDebounce: number;
  /** Table namespace — scopes query keys to prevent cross-table cache collisions */
  namespace: string;
}

export interface RandomAccessResult {
  totalRecords: number;
  getRow: (absoluteIndex: number) => Record<string, unknown> | null;
  requestPages: (startIndex: number, endIndex: number) => void;
  status: RandomAccessStatus;
  /** Flat array of all cached rows sorted by page (for selection system) */
  cachedRows: Record<string, unknown>[];
}

/** Shape returned by each page fetch */
interface PageResponse {
  items: Record<string, unknown>[];
  totalCount: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/** Match global queryClient — data stays fresh until explicit invalidation (refreshTable). */
const STALE_TIME = Infinity;
const CACHE_TIME = 120_000;
const QUERY_KEY_PREFIX = 'dt-v2-page';

/** No passive refetch on focus/reconnect; remount always refetches (navigate back). */
const PAGE_QUERY_OPTIONS = {
  staleTime: STALE_TIME,
  cacheTime: CACHE_TIME,
  refetchOnWindowFocus: false,
  refetchOnMount: 'always',
  refetchOnReconnect: false,
} as const;

function buildPageQueryKey(
  cacheRoot: readonly [string, string, string],
  querySignature: string,
  pageNum: number,
): readonly [string, string, string, string, number] {
  return [cacheRoot[0], cacheRoot[1], cacheRoot[2], querySignature, pageNum];
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useRandomAccessData(config: RandomAccessConfig): RandomAccessResult {
  const {
    dataSource,
    fetchParams,
    axiosInstance,
    refreshGeneration,
    externalDataProvided,
    pageSize,
    maxCachedPages,
    fetchDebounce,
    namespace,
  } = config;

  const queryClient = useQueryClient();

  // Force re-render trigger — incremented ONLY when background pages complete.
  // The counter value is used as a useMemo dependency for cachedRows.
  const [bgPageGeneration, forceRender] = useReducer((x: number) => x + 1, 0);

  // ── Query signature: deterministic string from filters/search/sort ─────
  const querySignature = useMemo(
    () => buildRandomAccessQuerySignature(fetchParams, pageSize),
    [fetchParams.searchText, fetchParams.filters, fetchParams.sort, pageSize],
  );

  const cacheRoot = useMemo(
    (): [string, string, string] => [
      QUERY_KEY_PREFIX,
      namespace,
      dataSource?.endpoint ?? '',
    ],
    [namespace, dataSource?.endpoint],
  );

  const fetchEnabled =
    !externalDataProvided &&
    dataSource != null &&
    !(dataSource.fetchPolicy === 'requireFilter' && !hasActiveQuery(fetchParams));

  // ── Refs for stable callbacks ──────────────────────────────────────────
  const querySignatureRef = useRef(querySignature);
  querySignatureRef.current = querySignature;
  const cacheRootRef = useRef(cacheRoot);
  cacheRootRef.current = cacheRoot;
  const fetchParamsRef = useRef(fetchParams);
  fetchParamsRef.current = fetchParams;
  const dataSourceRef = useRef(dataSource);
  dataSourceRef.current = dataSource;
  const axiosRef = useRef(axiosInstance);
  axiosRef.current = axiosInstance;

  // ── LRU page tracker ───────────────────────────────────────────────────
  const activePagesRef = useRef<number[]>([0]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingPagesRef = useRef<Set<number>>(new Set());

  // ── Page fetch function ────────────────────────────────────────────────
  const fetchPage = useCallback(async (pageNum: number, signal?: AbortSignal): Promise<PageResponse> => {
    const ds = dataSourceRef.current;
    if (!ds) return { items: [], totalCount: 0 };

    const p = fetchParamsRef.current;
    const queryParams = buildQueryParams(ds, p, pageNum, pageSize);
    const path = ds.endpoint.replace(/^\//, '');

    let response: { data: unknown };

    if (axiosRef.current) {
      const ax = axiosRef.current;
      if (ds.method === 'POST') {
        response = await ax.post(path, queryParams, { signal });
      } else {
        response = await ax.get(path, {
          params: queryParams,
          signal,
          paramsSerializer: serializeQueryParams,
        });
      }
    } else {
      const token = getToken();
      const baseUrl = process.env.REACT_APP_WEALTHLANE_API_ENDPOINT ?? '';
      const fullUrl = ds.endpoint.startsWith('http') ? ds.endpoint : `${baseUrl}/${path}`;
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const fetchUrl =
        ds.method === 'GET'
          ? `${fullUrl}?${serializeQueryParams(queryParams)}`
          : fullUrl;

      const fetchOptions: RequestInit = { method: ds.method, headers, signal };
      if (ds.method === 'POST') {
        fetchOptions.body = JSON.stringify(queryParams);
      }

      const raw = await fetch(fetchUrl, fetchOptions);
      if (!raw.ok) throw new Error(`Request failed: ${raw.status}`);
      response = { data: await raw.json() };
    }

    return parseListResponse(response.data, ds.responseMapping);
  }, [pageSize]);

  // ── Anchor query: page 0 ───────────────────────────────────────────────
  const anchorQueryKey = useMemo(
    () => buildPageQueryKey(cacheRoot, querySignature, 0),
    [cacheRoot, querySignature],
  );

  const anchorQuery = useQuery<PageResponse>({
    queryKey: anchorQueryKey,
    queryFn: ({ signal }) => fetchPage(0, signal as AbortSignal | undefined),
    keepPreviousData: true,
    ...PAGE_QUERY_OPTIONS,
    enabled: fetchEnabled,
  });

  // ── totalRecords ───────────────────────────────────────────────────────
  const totalRecords = fetchEnabled ? anchorQuery.data?.totalCount ?? 0 : 0;

  // ── Subscribe to query cache for background page completions ───────────
  // When a prefetched page settles, we need to re-render so getRow() picks
  // up the new data. Only fires forceRender when a tracked pending page
  // transitions to success — prevents re-render storms.
  useEffect(() => {
    const cache = queryClient.getQueryCache();
    const unsubscribe = cache.subscribe((queryOrEvent: any) => {
      if (!queryOrEvent) return;
      const query = queryOrEvent?.query ?? queryOrEvent;
      const key = query?.queryKey;

      if (
        Array.isArray(key) &&
        key[0] === QUERY_KEY_PREFIX &&
        key[1] === namespace &&
        key[2] === cacheRootRef.current[2] &&
        key[3] === querySignatureRef.current
      ) {
        const state = query?.state;
        if (state?.status === 'success') {
          const pageNum = key[4] as number;
          if (pendingPagesRef.current.has(pageNum)) {
            pendingPagesRef.current.delete(pageNum);
            forceRender();
          }
        }
      }
    });
    return unsubscribe;
  }, [queryClient, namespace]);

  // ── LRU touch + evict ──────────────────────────────────────────────────
  // Page 0 is pinned (anchor useQuery depends on it) — never evict it.
  const touchPages = useCallback((pages: number[]) => {
    const order = activePagesRef.current;

    for (const p of pages) {
      const idx = order.indexOf(p);
      if (idx !== -1) order.splice(idx, 1);
      order.push(p);
    }

    while (order.length > maxCachedPages) {
      const candidate = order[0];
      if (candidate === 0) {
        order.splice(0, 1);
        order.splice(1, 0, 0);
        if (order[0] === 0) break;
        continue;
      }
      order.shift();
      queryClient.removeQueries(
        buildPageQueryKey(cacheRootRef.current, querySignatureRef.current, candidate),
      );
    }
  }, [maxCachedPages, queryClient, namespace]);

  // ── requestPages: called by virtualizer on scroll ──────────────────────
  const requestPages = useCallback((startIndex: number, endIndex: number) => {
    if (!fetchEnabled) return;
    if (debounceRef.current != null) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      const sig = querySignatureRef.current;
      const { startPage, endPage } = computePageRange(startIndex, endIndex, pageSize);
      const pagesToFetch: number[] = [];

      for (let p = startPage; p <= endPage; p++) {
        pagesToFetch.push(p);
        const key = buildPageQueryKey(cacheRootRef.current, sig, p);
        const cached = queryClient.getQueryData(key);
        if (!cached && !pendingPagesRef.current.has(p)) {
          pendingPagesRef.current.add(p);
          queryClient.prefetchQuery({
            queryKey: key,
            queryFn: () => fetchPage(p),
            ...PAGE_QUERY_OPTIONS,
          });
        }
      }

      touchPages(pagesToFetch);
    }, fetchDebounce);
  }, [pageSize, fetchDebounce, queryClient, fetchPage, touchPages, fetchEnabled]);

  // ── getRow: random-access into React Query cache ───────────────────────
  const getRow = useCallback((absoluteIndex: number): Record<string, unknown> | null => {
    if (!fetchEnabled) return null;
    const pageNum = Math.floor(absoluteIndex / pageSize);
    const indexInPage = absoluteIndex % pageSize;
    const key = buildPageQueryKey(cacheRootRef.current, querySignatureRef.current, pageNum);
    const pageData = queryClient.getQueryData<PageResponse>(key);
    return pageData?.items?.[indexInPage] ?? null;
  }, [pageSize, queryClient, fetchEnabled]);

  // ── Invalidate on refreshGeneration change ─────────────────────────────
  const lastRefreshRef = useRef(refreshGeneration);
  useEffect(() => {
    if (lastRefreshRef.current !== refreshGeneration) {
      lastRefreshRef.current = refreshGeneration;
      pendingPagesRef.current.clear();
      queryClient.invalidateQueries([QUERY_KEY_PREFIX, namespace, cacheRoot[2]]);
    }
  }, [refreshGeneration, namespace, cacheRoot, queryClient]);

  // ── Reset LRU on query signature change ────────────────────────────────
  const lastSignatureRef = useRef(querySignature);
  useEffect(() => {
    if (lastSignatureRef.current === querySignature) return;
    const previousSignature = lastSignatureRef.current;
    lastSignatureRef.current = querySignature;
    activePagesRef.current = [0];
    pendingPagesRef.current.clear();

    queryClient.removeQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return (
          Array.isArray(key) &&
          key[0] === QUERY_KEY_PREFIX &&
          key[1] === namespace &&
          key[2] === cacheRoot[2] &&
          key[3] === previousSignature
        );
      },
    });

    if (fetchEnabled) {
      void queryClient.invalidateQueries({
        queryKey: buildPageQueryKey(cacheRoot, querySignature, 0),
      });
    }
  }, [querySignature, namespace, cacheRoot, queryClient, fetchEnabled]);

  // ── Cleanup debounce on unmount ────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (debounceRef.current != null) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // ── Status derivation ──────────────────────────────────────────────────
  const status: RandomAccessStatus = useMemo(() => {
    if (externalDataProvided || dataSource == null || !fetchEnabled) return 'idle';

    if (anchorQuery.isLoading && !anchorQuery.isPreviousData) {
      return 'initializing';
    }

    if (anchorQuery.isFetching && anchorQuery.isPreviousData) {
      return 'transitioning';
    }

    if (pendingPagesRef.current.size > 0) {
      return 'fetching';
    }

    return 'idle';
  }, [
    externalDataProvided, dataSource, fetchEnabled,
    anchorQuery.isLoading, anchorQuery.isFetching, anchorQuery.isPreviousData,
  ]);

  // ── cachedRows: flat array for selection system ────────────────────────
  const cachedRows = useMemo((): Record<string, unknown>[] => {
    if (!fetchEnabled) return [];
    const rows: Record<string, unknown>[] = [];
    const pages = [...activePagesRef.current].sort((a, b) => a - b);

    for (const pageNum of pages) {
      const key = buildPageQueryKey(cacheRootRef.current, querySignatureRef.current, pageNum);
      const pageData = queryClient.getQueryData<PageResponse>(key);
      if (pageData?.items) {
        for (const row of pageData.items) {
          rows.push(row);
        }
      }
    }

    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [namespace, queryClient, anchorQuery.dataUpdatedAt, bgPageGeneration, fetchEnabled]);

  return {
    totalRecords,
    getRow,
    requestPages,
    status,
    cachedRows,
  };
}
